import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, Clock, Mail, ArrowLeft, Scale } from 'lucide-react';

/**
 * /refund-policy — PricePAL Compare
 *
 * UK-consumer-law-grade refund policy.
 *
 * Covers:
 *   • Consumer Contracts (Information, Cancellation and Additional Charges)
 *     Regulations 2013 — 14-day cooling-off right for distance contracts.
 *   • Consumer Rights Act 2015 — statutory rights to refund where a digital
 *     service is not as described, not of satisfactory quality, or not fit
 *     for purpose.
 *   • Misrepresentation Act 1967 — refunds for misrepresentation.
 *
 * Last legal review: 4 June 2026 (Loyalty Pivot + full UK consumer rights
 * audit per owner instruction).
 */
export default function RefundPolicyPage() {
  return (
    <>
      <Helmet>
        <title>Refund Policy — PricePAL Compare</title>
        <meta
          name="description"
          content="PricePAL Compare refund policy: 14-day cooling-off period, CRA 2015 statutory refund rights, Stripe-backed self-service refunds. UK consumer-law compliant."
        />
      </Helmet>

      <main className="min-h-screen bg-gray-50 pb-20" data-testid="refund-policy-page">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-6"
            data-testid="refund-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">Refund Policy</h1>
            </div>

            <p className="text-xs text-gray-500 mb-2">Last updated: 4 June 2026</p>
            <p className="text-xs text-gray-500 mb-8">
              Governed by the laws of England &amp; Wales. Aligned with the Consumer Contracts
              (Information, Cancellation and Additional Charges) Regulations 2013 and the
              Consumer Rights Act 2015.
            </p>

            {/* Plain-English summary box */}
            <div
              className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-8 rounded-r-lg"
              data-testid="refund-summary-box"
            >
              <p className="font-semibold text-emerald-900 mb-1">Plain-English summary</p>
              <p className="text-sm text-emerald-900">
                If you've paid for a PricePAL Compare subscription and want your money back,
                <strong> you are legally entitled to a full refund within 14 days of your first payment</strong> —
                no questions asked, for any reason. After 14 days, you may still be entitled to a refund under
                the Consumer Rights Act 2015 if the service is faulty, not as described, or not fit for
                purpose. We aim to settle every reasonable refund request quickly and fairly.
              </p>
            </div>

            <section className="space-y-6 text-sm text-gray-700 leading-relaxed">
              {/* 1 — 14-day cooling-off */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  1. Your 14-Day Cooling-Off Right (Consumer Contracts Regulations 2013)
                </h2>
                <p className="mb-2">
                  As a UK consumer purchasing a digital subscription at a distance through{' '}
                  <a href="https://pricepalcompare.com" className="text-violet-600 hover:underline">
                    pricepalcompare.com
                  </a>{' '}
                  you have a statutory right to cancel <strong>within 14 calendar days</strong> of the day
                  you place your order and receive a <strong>full refund of the amount paid</strong>. This
                  applies to every initial subscription purchase — Standard, Premium, Pro, VIP Founder,
                  Early Founder, promotional and discounted tiers alike.
                </p>
                <p className="mb-2">
                  This right is unconditional and does not require you to give a reason.
                </p>
                <p className="text-xs text-gray-500 italic">
                  Regulatory basis: Regulations 29–38 of the Consumer Contracts (Information,
                  Cancellation and Additional Charges) Regulations 2013.
                </p>
              </div>

              {/* 2 — How to request */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  2. How to Request a Refund
                </h2>
                <p className="mb-3">You may request a refund in any of the following ways:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Self-service (fastest):</strong> Go to{' '}
                    <Link to="/settings" className="text-violet-600 hover:underline">
                      Settings → Subscription → Request Refund Now
                    </Link>
                    . If you are within the 14-day cooling-off window, the refund is approved
                    automatically and issued to your original payment method.
                  </li>
                  <li>
                    <strong>Email:</strong> Contact{' '}
                    <a
                      href="mailto:support@pricepalcompare.com"
                      className="text-violet-600 hover:underline"
                    >
                      support@pricepalcompare.com
                    </a>{' '}
                    from your account email. We will action your request within{' '}
                    <strong>14 calendar days</strong> of receiving it, in line with Regulation 34.
                  </li>
                  <li>
                    <strong>Post:</strong> PricePAL Compare, Refund Requests,{' '}
                    [registered business address available on request from support]. Statutory
                    written notice is honoured from the postmark date.
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  You may use the model cancellation form contained in Schedule 3 of the 2013 Regulations
                  if you prefer; it is not mandatory.
                </p>
              </div>

              {/* 3 — Refund Processing Time */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  3. Refund Processing Time
                </h2>
                <p className="mb-2">
                  Where you exercise the 14-day cooling-off right, we will reimburse you using the same
                  means of payment used for the original transaction <strong>within 14 days</strong> of
                  receiving your request, in compliance with Regulation 34(7).
                </p>
                <p>
                  In practice, refunds are processed immediately via Stripe. The amount typically appears
                  back on your card within <strong>5–10 business days</strong>, depending on your bank.
                  You will receive an email confirmation from us and a separate notification from Stripe.
                </p>
              </div>

              {/* 4 — Use of the service during the cooling-off period */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  4. Use of the Service During the Cooling-Off Period
                </h2>
                <p className="mb-2">
                  PricePAL Compare is a digital service. By starting a paid subscription and using Pro
                  features (such as receipt uploads, the AI Kitchen assistant or advanced fuel-tracker
                  views) during your first 14 days, you acknowledge that the service has begun to be
                  supplied within the cooling-off period. Where you cancel within the 14-day window:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    You remain entitled to a <strong>full refund of any amount paid</strong>;
                  </li>
                  <li>
                    Your subscription is set to <em>cancel at period end</em>, so you retain access until
                    the end of the billing period you originally paid for; after that date your account
                    automatically returns to the Free tier;
                  </li>
                  <li>
                    Any free Loyalty Pro Months credited to your account remain available for use should
                    you subsequently re-subscribe.
                  </li>
                </ul>
              </div>

              {/* 5 — Refunds after 14 days */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  5. Refunds After 14 Days — Consumer Rights Act 2015
                </h2>
                <p className="mb-2">
                  Even outside the 14-day cooling-off window, you remain entitled to a refund where:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    The service is <strong>not of satisfactory quality</strong>, including prolonged or
                    repeated unavailability of paid features;
                  </li>
                  <li>
                    The service is <strong>not as described</strong> on pricepalcompare.com or in any
                    promotional material (Misrepresentation Act 1967);
                  </li>
                  <li>
                    The service is <strong>not fit for the particular purpose</strong> you made known to
                    us before purchase;
                  </li>
                  <li>
                    You were charged in error, double-charged, or charged for a tier you did not select;
                  </li>
                  <li>
                    A renewal was charged after you submitted a valid cancellation request that we failed
                    to action.
                  </li>
                </ul>
                <p className="mt-3 text-sm">
                  Section 42 of the Consumer Rights Act 2015 entitles you in the first instance to
                  request that we repair or replace the digital service. Where repair/replacement is
                  impossible, disproportionate, or not provided within a reasonable time, you have the
                  right to a price reduction or full refund. PricePAL Compare will not require you to
                  exhaust repair/replacement remedies where doing so would cause significant
                  inconvenience — we will simply refund.
                </p>
              </div>

              {/* 6 — Renewals */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  6. Subscription Renewals
                </h2>
                <p className="mb-2">
                  Renewal payments (monthly or annual) are not automatically refunded by the self-service
                  flow, because the 14-day cooling-off right under the 2013 Regulations applies to your
                  initial purchase only.
                </p>
                <p className="mb-2">
                  <strong>However, you remain entitled to refunds on renewals under the Consumer Rights
                  Act 2015 (see Section 5).</strong> Where you have genuinely been unable to use the
                  service, or where a renewal occurred against your intention (e.g. you tried to cancel
                  and could not, or a payment was taken in error), please contact{' '}
                  <a
                    href="mailto:support@pricepalcompare.com"
                    className="text-violet-600 hover:underline"
                  >
                    support@pricepalcompare.com
                  </a>{' '}
                  — we will review and, in the majority of reasonable cases, refund the renewal in full
                  or pro-rata. Decisions are not arbitrary; we will provide a written reason if a
                  request is declined.
                </p>
                <p className="text-sm">
                  To avoid an unwanted renewal in the future, cancel your subscription at least 24 hours
                  before your next billing date via{' '}
                  <Link to="/settings" className="text-violet-600 hover:underline">
                    Settings
                  </Link>{' '}
                  or via the Stripe Billing Portal.
                </p>
              </div>

              {/* 7 — Founder & Promotional Tiers */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  7. Founder &amp; Promotional Tiers
                </h2>
                <p>
                  VIP Founder, Early Founder, and promotional-code subscriptions are governed by exactly
                  the same statutory refund rights set out above. There is no carve-out, fine print, or
                  reduced consumer protection for discounted subscriptions. If you cancel a Founder-tier
                  subscription during the cooling-off window, the grandfathered pricing is forfeited — you
                  will not be able to rejoin that tier once the cohort sells out, but your refund right
                  is not affected.
                </p>
              </div>

              {/* 8 — Loyalty rewards (non-monetary) */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  8. Loyalty &amp; Referral Rewards (Non-Monetary)
                </h2>
                <p className="mb-2">
                  The PricePAL Compare Loyalty &amp; Referral Rewards Programme issues rewards in{' '}
                  <strong>non-monetary form only</strong> — Loyalty Points (a gamification metric) and
                  Free Pro Subscription Months. <strong>The Programme itself does not constitute a
                  consumer purchase under the Consumer Contracts Regulations 2013</strong> because no
                  monetary consideration is exchanged for participation, and accordingly the rights set
                  out in this Refund Policy apply to your underlying paid subscription, not to Programme
                  rewards.
                </p>
                <p className="mb-2">
                  If you cancel a Pro subscription, any unused Free Pro Months remain attached to your
                  account and apply automatically should you subsequently re-subscribe. They are
                  non-transferable and not refundable in cash.
                </p>
                <p className="text-sm">
                  See Section 20 of our{' '}
                  <Link to="/terms" className="text-violet-600 hover:underline">
                    Terms &amp; Conditions
                  </Link>{' '}
                  for the full Programme rules.
                </p>
              </div>

              {/* 9 — Chargebacks */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">9. Chargebacks</h2>
                <p>
                  Please contact us <strong>before</strong> issuing a chargeback with your bank. Most
                  refund requests are resolved within minutes through the self-service flow or by email.
                  Issuing a chargeback for a payment that is eligible for, but has not yet been requested
                  via, our standard refund process is unnecessary and prevents us from supporting you.
                  Your statutory rights under the 2013 Regulations and the 2015 Act are unaffected by
                  this paragraph.
                </p>
              </div>

              {/* 10 — Complaints */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  10. Complaints &amp; Independent Resolution
                </h2>
                <p className="mb-2">
                  If you are unhappy with how a refund request has been handled, please first contact{' '}
                  <a
                    href="mailto:support@pricepalcompare.com"
                    className="text-violet-600 hover:underline"
                  >
                    support@pricepalcompare.com
                  </a>{' '}
                  with the words "<em>formal complaint</em>" in your subject line. We will issue a
                  written response within 8 weeks.
                </p>
                <p className="mb-2">
                  Should the matter remain unresolved, you may refer your complaint to:
                </p>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li>
                    <strong>Citizens Advice consumer helpline</strong> — 0808 223 1133 —{' '}
                    <a
                      href="https://www.citizensadvice.org.uk"
                      className="text-violet-600 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      citizensadvice.org.uk
                    </a>
                  </li>
                  <li>
                    <strong>Online Dispute Resolution platform (EU/UK):</strong>{' '}
                    <a
                      href="https://ec.europa.eu/consumers/odr/"
                      className="text-violet-600 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      ec.europa.eu/consumers/odr
                    </a>
                  </li>
                  <li>
                    <strong>Small Claims Court (England &amp; Wales)</strong> for claims up to £10,000.
                  </li>
                </ul>
              </div>

              {/* 11 — Statutory rights preserved */}
              <div>
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-2">
                  11. Statutory Rights Are Preserved
                </h2>
                <p>
                  Nothing in this Refund Policy excludes or limits your statutory rights under the
                  Consumer Rights Act 2015, the Consumer Contracts (Information, Cancellation and
                  Additional Charges) Regulations 2013, the Misrepresentation Act 1967, the Unfair
                  Contract Terms Act 1977, or any other applicable UK consumer-protection legislation.
                  Where any term in this Policy conflicts with your statutory rights, your statutory
                  rights prevail.
                </p>
              </div>
            </section>

            <div className="mt-10 border-t pt-6 grid sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 text-xs text-gray-600">
                <Clock className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>14-day cooling-off — automatic self-service refund via Settings.</span>
              </div>
              <div className="flex items-start gap-3 text-xs text-gray-600">
                <Scale className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>Statutory CRA 2015 rights preserved beyond the 14-day window.</span>
              </div>
              <div className="flex items-start gap-3 text-xs text-gray-600">
                <Mail className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span>
                  Questions?{' '}
                  <a
                    href="mailto:support@pricepalcompare.com"
                    className="text-violet-600 hover:underline"
                  >
                    support@pricepalcompare.com
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
